import { historyComment } from "../../utils/historyComment";
import { historyAuthor } from "../../utils/historyAuthor";
import ClientTabHeading from "./ClientTabHeading";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Clock, Plus, RefreshCw } from "lucide-react";
import { message } from "antd";
import axios from "../../axios/AxiosInstance";
import { CommentFormModal } from "../CommentFormModal";

export function buildClientHistory(client, commercial = {}, actions = []) {
  const ids = new Set([client._id,client.id,client.client_id,client.establishment_id].filter(Boolean).map(String));
  const matches = row => ids.has(String(row.clientId?._id || row.clientId?.id || row.clientId || row.establishment_id?._id || row.establishment_id || ""));
  const entries = [];
  const add = (category, title, date, detail = "", author = "", extra = {}) =>
    entries.push({
      id: `${category}-${entries.length}`,
      category,
      title,
      date,
      detail,
      author,
      ...extra,
    });
  (client.comments || []).forEach((row) =>
    add("Commentaire", row.comment, row.createdAt, "", row.commentator),
  );
  (commercial.events || [])
    .filter(matches)
    .forEach((row) =>
      add(
        "Actions commerciales",
        row.label || row.type || "Action commerciale",
        row.createdAt || row.at,
        historyComment(row, (commercial.results || []).find(result => row.resultId && String(result.id || result._id) === String(row.resultId)) || {}),
        historyAuthor(row, commercial.users, commercial.results),
      ),
    );
  const represented = new Set((commercial.events || []).filter(matches).map(row => String(row.resultId || "")));
  (commercial.results || []).filter(matches).filter(row => !represented.has(String(row.id || row._id))).forEach(row => add("Résultat d’appel", row.label || "Appel", row.createdAt, historyComment(row), historyAuthor(row, commercial.users)));
  const taskIds = new Set(actions.map((row) => String(row.taskId)));
  (client.tasks || [])
    .filter((row) => !taskIds.has(String(row._id || row.id)))
    .forEach((row) =>
      add(
        "Tâche",
        row.title || row.type || "Tâche",
        row.date || row.createdAt,
        row.comment || row.status,
      ),
    );
  actions.forEach((row) => {
    const title = row.title || row.type || "Action";
    const details = [
      ...new Set(
        [row.comment !== title ? row.comment : "", row.description, row.detail]
          .map((value) => String(value || "").trim())
          .filter(Boolean),
      ),
    ].join("\n\n");
    add("Tâche", title, row.at, details, historyAuthor(row, commercial.users), {
      status: row.action,
      dueDate: row.date,
      hour: row.hour,
      previousDate: row.action === "Reportée" ? row.previousDate : null,
      previousHour: row.previousHour,
    });
  });
  (commercial.needs || [])
    .filter(matches)
    .forEach((row) =>
      add(
        "Demande de personnel",
        row.job || "Besoin identifié",
        row.createdAt,
        row.status,
      ),
    );
  (client.contacts || [])
    .filter((row) => row.active === false)
    .forEach((row) =>
      add(
        "Contact",
        `${[row.firstname, row.lastname].filter(Boolean).join(" ") || "Contact"} · Parti`,
        row.retiredAt,
      ),
    );
  if (client.createdAt)
    add("Client", "Création de la fiche client", client.createdAt);
  const timestamp = (value) =>
    typeof value === "number" ? value : Date.parse(value) || 0;
  return entries.sort((a, b) => timestamp(b.date) - timestamp(a.date));
}
const dateLabel = (value) => {
  if (!value) return "Date non renseignée";
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "Date non renseignée";
};
export default function ClientHistory({ client, clients, setClients }) {
  const [comments, setComments] = useState(client.comments || []);
  const [commercial, setCommercial] = useState({});
  const [actions, setActions] = useState([]);
  const [loadErrors, setLoadErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("Tout");
  const [open, setOpen] = useState(false);
  const generation = useRef(0);
  const refresh = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    const results = await Promise.allSettled([
      axios.get(`commercial/clients/${encodeURIComponent(client._id)}/history`),
      axios.get(`newtask/history/client/${client._id}`),
    ]);
    if (current !== generation.current) return;
    const errors = [];
    if (results[0].status === "fulfilled")
      setCommercial(results[0].value.data || {});
    else errors.push("Le suivi commercial n’a pas pu être chargé.");
    if (results[1].status === "fulfilled")
      setActions(
        Array.isArray(results[1].value.data) ? results[1].value.data : [],
      );
    else
      errors.push(
        "Les actions et leurs commentaires n’ont pas pu être chargés.",
      );
    setLoadErrors(errors);
    setLoading(false);
  }, [client._id]);
  useEffect(() => {
    setComments(client.comments || []);
  }, [client._id, client.comments]);
  useEffect(() => {
    setCommercial({});
    setActions([]);
    setFilter("Tout");
    setLoadErrors([]);
    refresh();
    window.addEventListener("jm-task-updated", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      generation.current++;
      window.removeEventListener("jm-task-updated", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);
  const save = async (comment) => {
    const next = [comment, ...comments];
    const { data } = await axios.post(`client/${client._id}/update-comment`, {
      comments: next,
    });
    setComments(next);
    setFilter("Tout");
    setClients?.(
      (clients || []).map((row) =>
        row._id === client._id ? { ...row, ...data, comments: next } : row,
      ),
    );
    message.success("Commentaire enregistré.");
  };
  const entries = buildClientHistory(
    { ...client, comments },
    commercial,
    actions,
  );
  const visible = entries.filter(
    (entry) => filter === "Tout" || entry.category === filter,
  );
  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ClientTabHeading
          title="Historique"
          description="Commentaires, actions et échanges, du plus récent au plus ancien."
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={refresh}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white"
          >
            <Plus size={16} />
            Ajouter un commentaire
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {[
          ...new Set([
            "Tout",
            "Commentaire",
            "Tâche",
            "Actions commerciales",
            "Client",
            "Contact",
            ...entries.map((row) => row.category),
          ]),
        ].map((category) => (
          <button
            type="button"
            key={category}
            aria-pressed={filter === category}
            onClick={() => setFilter(category)}
            className={`rounded-full px-3 py-1.5 text-sm ${filter === category ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-600"}`}
          >
            {category}
          </button>
        ))}
      </div>
      {!!loadErrors.length && (
        <p role="alert" className="text-sm text-red-700">
          {loadErrors.join(" ")} Cliquez sur Actualiser pour réessayer.
        </p>
      )}
      {loading && (
        <p role="status" className="text-sm text-slate-500">
          Chargement de l’historique…
        </p>
      )}
      {visible.length ? (
        <ol className="space-y-3">
          {visible.map((entry) => (
            <li
              key={entry.id}
              className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4"
            >
              <span className="h-fit rounded-lg bg-blue-50 p-2 text-blue-700">
                <Clock size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-blue-50 px-2 py-1 font-medium text-blue-700">
                    {entry.category}
                    {entry.status ? ` · ${entry.status}` : ""}
                  </span>
                  <span className="text-slate-500">
                    {dateLabel(entry.date)}
                    {` · Par ${entry.author || "Auteur non renseigné"}`}
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm font-medium text-slate-900">
                  {entry.title}
                </p>
                {entry.detail && (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">
                    {entry.detail}
                  </p>
                )}
                {entry.dueDate && (
                  <p className="mt-2 text-xs text-slate-500">
                    Échéance :{" "}
                    {new Date(entry.dueDate).toLocaleDateString("fr-FR")}{" "}
                    {entry.hour}
                  </p>
                )}
                {entry.previousDate && (
                  <p className="mt-1 text-xs text-slate-500">
                    Ancienne échéance :{" "}
                    {new Date(entry.previousDate).toLocaleDateString("fr-FR")}{" "}
                    {entry.previousHour}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        !loading && (
          <p className="rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">
            Aucune activité pour ce filtre.
          </p>
        )
      )}
      <CommentFormModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onSave={save}
      />
    </section>
  );
}
